import * as momentTz from 'moment-timezone';
import { Component, EventEmitter, Inject, Input, OnInit, AfterViewInit, Output } from 'ng-metadata/core';
import './date-range-selector-dropdown.scss';

/**
 * @ngdoc directive
 * @name mwDateRangeSelectorDropdown
 * @module mw.components.dateRange
 *
 * @param {Object} [date-range] - A selected date range option.  If provided, this will show a checkmark next to the appropriate date range option in the menu.  The date range option object should have the following structure, {name: string, type: string, startDate: Date, endDate: Date}.
 * @param {string|Object[]} [date-range-types] - Array of Date Range type options to display in the menu.  Supported values include a set of predefined types or custom Date Range option object.  Predefined types include 'today', 'thisWeek', 'last7Days', 'last14Days', 'thisMonth', 'last30Days', 'last90Days', 'thisQuarter', 'lastQuarter', 'thisYear', 'lastYear' and 'custom'.  (Default: =['today','thisWeek','last7Days','thisMonth','last30Days', 'divider', 'custom']).  The custom date range option object should have the following structure, {name: string, type: string, startDate?: Date, endDate?: Date}.
 * @param {expression} [on-date-range-select] - Angular expression to evaluate on select of a date range menu item. $event is the DateRangeOption object representing the selected date range option.
 * @param {string|number} [width] - determines the width of the date-range-selector dropdown when input is selected
 * @param {Object} [custom-range-picker-options] - custom options related to the date-range picker, includes locale, start-date, end-date, min-view, max-view, min-date, max-date, max-days
 * @param {Object} [custom-range-dialog-options] - custom options related to the dialog of the date range picker
 * @param {string} [date-range-format] - The format of the selected start and end date as the input value.  This format is used when both start and end dates are in the current year.
 * @param {string} [date-range-format-with-year] - The format of the selected start and end date as the input value, including year.  This format is used when start or end date are not in the current year.
 * @param {string} [input-name] - Name of the text input
 * @param {Object} [required] - Value for 'required' in the text input
 * @param {string} [date-range-label] - ngModel of the text input
 * @param {string} [locale] - The locale/language that the custom date range picker will use
 * @param {string} [timezone] - The timezone to be used by the custom date range picker. Valid momentjs
 *    timezones (see https://momentjs.com/timezone/docs/#/using-timezones/parsing-in-zone/) are supported.  If not specified, the timezone of the browser will be used.
 * @param {boolean} [is-error] - The value is added in 'md-is-error' into the 'md-input-container' to know if the error must show
 * @param {boolean} [limit-end-to-now=false] - If true, future date/time is not allowed and the end date time will automtically be set to 'now' (the moment the user selects the date range)
 *
 * @description
 * `mw-date-range-selector-dropdown` provides the user with a form input that allows for the user to select their date-range similar to the `mw-date-range-selector`
 * @usage
 * <hljs hljs-langauge="html">
 *  <mw-date-range-selector-dropdown date-range="$ctrl.selectedDateRange" on-date-range-select="$ctrl.selectedDateRange = $event"> </mw-date-range-form-selector>
 * </hljs>
 */
@Component({
    selector: `mw-date-range-selector-dropdown`,
    template: `
      <md-menu md-position-mode="target-left target" md-offset="0 40">
        <md-input-container ng-click="$mdMenu.open($event)" ng-focus="$mdMenu.open($event)" class="mw-date-range-selector-dropdown__container" md-is-error="$ctrl.isError">
            <ng-transclude ng-transclude-slot="label">
                <label translate>Date Range</label>
            </ng-transclude>
            <input ng-model="$ctrl.dateRangeLabel" class="mw-date-range-selector-dropdown__input" name="{{::$ctrl.inputName}}" ng-required="::$ctrl.required"/>
            <button
                class="mw-date-range-selector-dropdown__triangle-button md-icon-button md-button"
                ng-click="$mdMenu.open($event)"
            >
                <div class="mw-date-range-selector-dropdown__triangle ng-scope"> </div>
            </button>
            <ng-transclude ng-transclude-slot="validationMessage"></ng-transclude>
        </md-input-container>

        <md-menu-content ng-style="{'width': $ctrl.width + 'px'}">
            <mw-date-range-selector
                relative-date="$ctrl.relativeDate"
                on-select="$ctrl.handleDateSelection($event)"
                date-range="$ctrl.dateRange"
                locale="$ctrl.locale"
                timezone="$ctrl.timezone"
                no-padding="true"
                date-range-types="$ctrl.dateRangeTypes"
                limit-end-to-now="$ctrl.limitEndToNow"
                custom-range-picker-options="$ctrl.customRangePickerOptions"
                custom-range-dialog-options="$ctrl.customRangeDialogOptions"
                width="$ctrl.width"
            > </mw-date-range-selector>
        </md-menu-content>
      </md-menu>
    `,
    legacy: {
        transclude: {
            'label': '?label',
            'validationMessage': '?validationMessage'
        }
    }
})
export class DateRangeSelectorDropdownComponent implements OnInit, AfterViewInit {
    @Input('<') dateRange: DateRange;
    @Input('<') dateRangeTypes;
    @Input('<') customRangePickerOptions: Object;
    @Input('<') customRangeDialogOptions: Object;
    @Input('<') width: string|number = 352;
    @Input('<') dateRangeFormat: String = 'MMM D';
    @Input('<') dateRangeFormatWithYear: String = 'MMM D, YYYY';
    @Input('<') locale: string;
    @Input('<') timezone: string;
    @Input('@') inputName: string;
    @Input('@') required: any;
    @Input('=') dateRangeLabel: string;
    @Input('<') isError: boolean;
    @Input('<') limitEndToNow: boolean;
    @Output() onDateRangeSelect = new EventEmitter();

    private dateRangePipe;
    private isDropdownOpen = false;
    private relativeDate;

    constructor(
        @Inject('$element') private $element: ng.IRootElementService,
        @Inject('$filter') private $filter: ng.IFilterService,
        @Inject('$scope') private $scope,
        @Inject('$timeout') private $timeout,
        @Inject('$translate') private $translate) {

            this.updateLabel = this.updateLabel.bind(this);
    }

    ngOnInit() {
        this.dateRangePipe = this.$filter('mwDateRange');

        this.$scope.$on('$mdMenuOpen', (event, menuEl) => {
            if (this.$element[0] === menuEl.parent()[0]) {
                this.$element.find('md-input-container')[0].classList.add('md-input-focused');
                this.isDropdownOpen = true;
            }

            this.relativeDate = momentTz().tz(this.timezone).toDate();
        });

        this.$scope.$on('$mdMenuClose', (event, menuEl) => {
            if (this.$element[0] === menuEl.parent()[0]) {
                this.$element.find('md-input-container')[0].classList.remove('md-input-focused');
                this.$element.find('input')[0].focus();

                // This needs to be in a timeout to give the close animation a chance to complete
                this.$timeout(() => {this.isDropdownOpen = false}, 250);
            }
        });

        if (this.dateRange) {
            this.dateRangeLabel = this.setDateRangeLabel(this.dateRange);
        }

        this.required = this.required ? this.required : false;
    }

    ngAfterViewInit() {
        this.$element.find('input')[0].addEventListener('keydown', (e) => e.preventDefault());
        this.$element.find('md-input-container')[0].addEventListener('click', (e) => this.$element.find('md-input-container')[0].classList.add('md-input-focused'));
    }

    setDateRangeLabel(dateRange) {
        if (dateRange.name && dateRange.name !== 'Custom') {
            return this.$translate.instant(dateRange.name);
        } else {
            let thisYear = (new Date()).getFullYear();
            let dateFormat = (dateRange.startDate.getFullYear() !== thisYear || dateRange.endDate.getFullYear() !== thisYear) ? this.dateRangeFormatWithYear : this.dateRangeFormat;
            return this.dateRangePipe(dateRange, dateFormat, this.timezone, this.locale);
        }
    }

    handleDateSelection(event) {
        this.dateRangeLabel = this.setDateRangeLabel(event);
        this.onDateRangeSelect.emit({startDate: event.startDate, endDate: event.endDate, type: event.type, name: event.name});
    }

    updateLabel(dateRange: string) {
        this.dateRangeLabel = dateRange;
    }
}



// WEBPACK FOOTER //
// ./src/lib/components/date-range/date-range-selector-dropdown/date-range-selector-dropdown.component.ts